import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { del, get, patch } from "../lib/api";
import { usePermission } from "../hooks/useSession";
import type { CollectionSummary } from "../lib/types";
import { Sep, Shell } from "../components/Shell";
import { Upload } from "../components/Upload";
import { AddDocuments } from "../components/AddDocuments";
import { DocumentsList } from "../components/DocumentsList";
import { Button, Chip, Failed, Loading } from "../components/ui";

export function CollectionDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const can = usePermission();
  const canManage = can("collections:manage");
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const listing = useQuery({
    queryKey: ["collections", "all"],
    queryFn: () => get<{ collections: CollectionSummary[] }>("/v1/collections?archived=true"),
  });
  const collection = listing.data?.collections.find((c) => c.id === id);

  const save = useMutation({
    mutationFn: () =>
      patch<CollectionSummary>(`/v1/collections/${id}`, { name, description: description || null }),
    onSuccess: () => {
      setEditing(false);
      void client.invalidateQueries({ queryKey: ["collections"] });
    },
  });
  const archive = useMutation({
    mutationFn: () => del(`/v1/collections/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["collections"] });
      navigate("/collections");
    },
  });
  // The way back. Archiving keeps the membership rows, so it has to be reversible; the
  // 409 here is a live collection having taken the name while this one was away.
  const unarchive = useMutation({
    mutationFn: () => patch<CollectionSummary>(`/v1/collections/${id}`, { archived: false }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["collections"] }),
  });

  if (listing.isPending) {
    return (
      <Shell crumb={<span>collections</span>}>
        <Loading what="the collection" />
      </Shell>
    );
  }
  if (listing.isError || !collection) {
    return (
      <Shell crumb={<span>collections</span>}>
        <Failed error={listing.error} what="the collection" />
      </Shell>
    );
  }

  return (
    <Shell
      fill
      crumb={
        <>
          <Link to="/collections" className="transition-colors hover:text-gold">
            collections
          </Link>
          <Sep />
          <span className="truncate text-text normal-case">{collection.name}</span>
        </>
      }
    >
      <div className="flex flex-wrap items-start justify-between gap-x-5 gap-y-2">
        <div className="min-w-0">
          {editing ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (name.trim() !== "") save.mutate();
              }}
              className="flex flex-col gap-2"
            >
              <input
                aria-label="collection name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="border border-hair bg-transparent px-2 py-1 font-mono text-[13px]"
              />
              <textarea
                aria-label="collection description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                rows={2}
                className="border border-hair bg-transparent px-2 py-1 font-mono text-[11.5px]"
              />
              <div className="flex gap-2">
                <Button type="submit" variant="primary" disabled={save.isPending}>
                  save
                </Button>
                <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
                  cancel
                </Button>
              </div>
            </form>
          ) : (
            <>
              <h2 className="truncate text-[1.35rem] leading-none tracking-tighter uppercase">
                {collection.name}
              </h2>
              <div className="mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-1.5 font-mono text-[10.5px] text-faint">
                <span>{collection.document_count} documents</span>
                {collection.archived_at && <Chip tone="mut">archived</Chip>}
                {unarchive.isError && (
                  <span className="text-crit">
                    {unarchive.error instanceof Error ? unarchive.error.message : "could not restore"}
                  </span>
                )}
                {collection.description && <span className="text-dim">{collection.description}</span>}
              </div>
            </>
          )}
        </div>
        {!editing && (
          <div className="flex gap-2">
            {/* Not behind collections:manage: putting a document into a collection is
                documents:write, the same permission tagging needs. The bulk call refuses
                it server-side either way. */}
            <Button variant="primary" onClick={() => setAdding(true)}>
              add documents
            </Button>
            {canManage && (
              <>
            <Button
              onClick={() => {
                setName(collection.name);
                setDescription(collection.description ?? "");
                setEditing(true);
              }}
            >
              edit
            </Button>
            {collection.archived_at ? (
              <Button
                disabled={unarchive.isPending}
                onClick={() => unarchive.mutate()}
                title="Bring this collection back into the rail and the pickers"
              >
                {unarchive.isPending ? "restoring…" : "unarchive"}
              </Button>
            ) : (
              <Button
                variant="ghost"
                disabled={archive.isPending}
                onClick={() => archive.mutate()}
                className="hover:!text-crit"
              >
                archive
              </Button>
            )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Uploading here parses the file into this collection, which is also what makes
          the table's "drop a file above" true on this screen. */}
      <Upload collectionId={id} />

      {adding && <AddDocuments collectionId={id} onDone={() => setAdding(false)} />}

      <DocumentsList
        pinnedCollection={id}
        empty={{
          title: "nothing in this collection yet",
          hint: "Upload a file above to parse it into this collection, or add a document you have already uploaded.",
          action: { label: "add documents", onClick: () => setAdding(true) },
        }}
      />
    </Shell>
  );
}
